import React from "react";
import Flex from "src/sudokit/Layout/Flex";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { CUSTOM_STYLE_PRESET_ID, CUSTOM_STYLE_PRESET_OPTION, StylePresetOption } from "./types";

interface StylePresetEditorProps {
  variantLabel?: string;
  presets: StylePresetOption[];
  presetsLoading: boolean;
  presetsError?: string;
  stylePresetId: string;
  onStylePresetIdChange: (id: string) => void;
  text: string;
  onTextChange: (value: string) => void;
  onResetText: () => void;
  isTextDefault: boolean;
  variantHelpText?: string;
  promptHelpText?: string;
}

const StylePresetEditor: React.FC<StylePresetEditorProps> = ({
  variantLabel = "Style Variant",
  presets,
  presetsLoading,
  presetsError,
  stylePresetId,
  onStylePresetIdChange,
  text,
  onTextChange,
  onResetText,
  isTextDefault,
  variantHelpText,
  promptHelpText,
}) => (
  <>
    <div className="flex flex-col items-start gap-2 w-full text-left">
      <Text size="sm" weight="medium">
        {variantLabel}
      </Text>
      {presetsLoading ? (
        <Flex align="center" gap={2}>
          <Spinner size="sm" />
          <Text size="sm" color="grey-1">
            Loading style variants…
          </Text>
        </Flex>
      ) : presetsError ? (
        <Text size="sm" color="danger-color">
          Couldn't load style presets: {presetsError}. Is the API server running?
        </Text>
      ) : (
        <MenuSelect
          options={[...presets, CUSTOM_STYLE_PRESET_OPTION].map((preset) => ({ value: preset.id, label: preset.label }))}
          value={stylePresetId}
          onChange={onStylePresetIdChange}
          label={variantLabel}
          variant="secondary"
          menuAlign="start"
        />
      )}
      {variantHelpText && (
        <Text size="xs" color="grey-1">
          {variantHelpText}
        </Text>
      )}
    </div>

    <div className="flex flex-col gap-2 w-full text-left">
      <Flex align="center" justify="space-between" className="w-full">
        <Text size="sm" weight="medium">
          Style Prompt
        </Text>
        {!isTextDefault && (
          <SudoButton type="button" variant="tertiary" size="xsmall" onClick={onResetText}>
            Reset to default
          </SudoButton>
        )}
      </Flex>
      <Textarea
        mode="default"
        value={text}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onTextChange(e.target.value)}
        placeholder={stylePresetId === CUSTOM_STYLE_PRESET_ID ? "Write a style prompt from scratch…" : undefined}
        minRows={10}
        className="w-full font-mono text-xs"
      />
      {promptHelpText && (
        <Text size="xs" color="grey-1">
          {promptHelpText}
        </Text>
      )}
    </div>
  </>
);

export default StylePresetEditor;
